import { Config } from "@config";
import { catchCalculatorError } from "@component/assets/AbstractListingCalculator/domain";
import type { ResultListResponse } from "@component/assets/AbstractListingCalculator/types";
import { faCirclePlus as fasCirclePlus } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { StringPair } from "@utils/JsonDataBuilder";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import { Console } from "@utils/console";
import { http } from "bm-ui-kit";
import kebabCase from "lodash/kebabCase";
import type { ReactNode } from "react";
import React from "react";
import type { SmeAccountLeadCalcRequest, SmeAccountLeadCalcResponse } from "../LeadForm/types";
import { OUTGOING_TRANSFER } from "../SmallCalculator/Steps/Step1";
import PROFILES from "./profiles/profiles_simple.json";
import type {
	CorporateBankAccountRequest,
	CorporateBankAccountResponseItem,
	CorporateBankAccountSearchParams,
	CorporateBankAccountSort,
	Fee,
} from "./types";

function getProductNameByProfile(profile: string): string[] {
	return PROFILES[profile];
}

export function getProfileBySearchParams(searchParams: CorporateBankAccountSearchParams): string {
	let letter = "";

	switch (searchParams.outgoingTransfer) {
		case "+50":
			letter = "A";
			break;
		case "20-50":
			letter = "B";
			break;
		case "10-20":
			letter = "C";
			break;
		case "5-10":
			letter = "D";
			break;
		case "1,5-5":
			letter = "E";
			break;
		case "1,5":
			letter = "F";
			break;
		default:
	}

	return `PROFILE_${letter}`;
}

export async function calculate(
	request: CorporateBankAccountRequest,
	_searchParams: CorporateBankAccountSearchParams,
): Promise<any> {
	return http
		.get(Config.getApiUrl("/api/public/corporate-account/product-listing", request))
		.catch(catchCalculatorError);
}

export function responseToResult(
	_request: CorporateBankAccountRequest,
	searchParams: CorporateBankAccountSearchParams,
	response: any,
): ResultListResponse<CorporateBankAccountResponseItem> {
	const products = getProductNameByProfile(getProfileBySearchParams(searchParams))?.map((name) => kebabCase(name));

	const results = response.filter((item) => products?.includes(kebabCase(`${item.bank.name} - ${item.name}`)));

	Console.log("PROFILE: ", getProfileBySearchParams(searchParams));
	Console.log(
		"MISSING: ",
		products?.filter(
			(product) => !response.map((item) => kebabCase(`${item.bank.name} - ${item.name}`)).includes(product),
		),
	);

	return {
		highlightList: results.filter((result) => result.marketing?.highlight),
		normalList: results.map((result) => ({
			...result,
			marketing: { ...result.marketing, highlight: false, newTargetUrl: undefined },
		})),
	};
}

export function getLeadCalculatorRequest(searchParams: CorporateBankAccountSearchParams): SmeAccountLeadCalcRequest {
	const otherData = new JsonDataBuilder();

	otherData.addItem(
		"Mekkora a céged havi átlag kimenő forgalma?",
		"outgoingTransfer",
		OUTGOING_TRANSFER.find((item) => item.value === searchParams.outgoingTransfer)?.label,
	);

	return { companyName: undefined, otherDataJson: otherData.getJson() };
}

export function getLeadCalculatorResponse(response: CorporateBankAccountResponseItem): SmeAccountLeadCalcResponse {
	return {
		id: response?.id,
		productName: response?.name,
		bankCode: response?.bank?.code,
		bankName: response?.bank?.name,
	};
}

export function searchParamsToRequest(
	searchParams: CorporateBankAccountSearchParams,
	sort: CorporateBankAccountSort = undefined,
): CorporateBankAccountRequest {
	return {
		...searchParams,
		banks: undefined,
		sort,
		roleCode: "BM",
	};
}

export async function exportXls(searchParams: CorporateBankAccountSearchParams): Promise<string> {
	const request: CorporateBankAccountRequest = searchParamsToRequest(searchParams);

	return http.get(Config.getApiUrl("/api/private/corporate-account/export", request));
}

export const CorporateBankAccountsInfo = {
	getSearchParametersText: (_parameters: CorporateBankAccountSearchParams): StringPair[] => [],

	getProductParametersText: (
		_parameters: CorporateBankAccountSearchParams,
		response: CorporateBankAccountResponseItem,
	): StringPair[] => {
		const data = new JsonDataBuilder();
		data.addTextItem("Nyitási díj", response.accountOpeningFee, "ft");
		data.addTextItem("Éves díjak", response.hufFeeSum, "ft");

		return data.getDataArray();
	},
};

export function renderFee(data: Fee, stringMode: boolean = false): string | ReactNode {
	if (data.duty !== "-") {
		if (stringMode) {
			return `${data.fee}, ${data.duty}`;
		}

		return (
			<>
				{data.fee} <br />
				<FontAwesomeIcon icon={fasCirclePlus} />
				<br />
				{data.duty}
			</>
		);
	}

	return data.fee;
}
